import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { PageLoadingFallback } from "@/components/ui/loading-skeleton";
import { ErrorBoundary } from "@/components/ui/error-boundary";


// Lazy load pages for better initial bundle size
const Dashboard = lazy(() => import("./pages/Dashboard"));
const FluxoCaixa = lazy(() => import("./pages/FluxoCaixa"));
const ContasPagar = lazy(() => import("./pages/ContasPagar"));
const Veiculos = lazy(() => import("./pages/Veiculos"));

const Producao = lazy(() => import("./pages/Producao"));
const Relatorios = lazy(() => import("./pages/Relatorios"));
const Fiscal = lazy(() => import("./pages/Fiscal"));
const Rastreabilidade = lazy(() => import("./pages/Rastreabilidade"));
const Estoque = lazy(() => import("./pages/Estoque"));
const FretesTaxi = lazy(() => import("./pages/FretesTaxi"));
const Licencas = lazy(() => import("./pages/Licencas"));
 const Recuperacao = lazy(() => import("./pages/Recuperacao"));
const Admin = lazy(() => import("./pages/Admin"));
const Funcionarios = lazy(() => import("./pages/Funcionarios"));
const Auth = lazy(() => import("./pages/Auth"));
const PendingApproval = lazy(() => import("./pages/PendingApproval"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 2, // 2 minutes (consistent with memory)
      gcTime: 1000 * 60 * 10, // 10 minutes (consistent with memory)
      refetchOnWindowFocus: false,
    },
  },
});

// Wrapper component with Error Boundary for each module
const ModuleBoundary = ({ 
  children, 
  name 
}: { 
  children: React.ReactNode; 
  name: string;
}) => (
  <ErrorBoundary moduleName={name}>
    {children}
  </ErrorBoundary>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<PageLoadingFallback />}>
            <Routes>
              <Route path="/auth" element={<Auth />} />
              <Route path="/pending-approval" element={<PendingApproval />} />
              <Route path="/" element={
                <ProtectedRoute>
                  <ModuleBoundary name="Dashboard">
                    <Dashboard />
                  </ModuleBoundary>
                </ProtectedRoute>
              } />
              <Route path="/fluxo-caixa" element={
                <ProtectedRoute>
                  <ModuleBoundary name="Fluxo de Caixa">
                    <FluxoCaixa />
                  </ModuleBoundary>
                </ProtectedRoute>
              } />
              <Route path="/contas-pagar" element={
                <ProtectedRoute>
                  <ModuleBoundary name="Contas a Pagar">
                    <ContasPagar />
                  </ModuleBoundary>
                </ProtectedRoute>
              } />
              <Route path="/veiculos" element={
                <ProtectedRoute>
                  <ModuleBoundary name="Veículos">
                    <Veiculos />
                  </ModuleBoundary>
                </ProtectedRoute>
              } />
              <Route path="/producao" element={
                <ProtectedRoute>
                  <ModuleBoundary name="Produção">
                    <Producao />
                  </ModuleBoundary>
                </ProtectedRoute>
              } />
               <Route path="/recuperacao" element={
                 <ProtectedRoute>
                   <ModuleBoundary name="Recuperação">
                     <Recuperacao />
                   </ModuleBoundary>
                 </ProtectedRoute>
               } />
              <Route path="/funcionarios" element={
                <ProtectedRoute requireAdmin>
                  <ModuleBoundary name="Funcionários">
                    <Funcionarios />
                  </ModuleBoundary>
                </ProtectedRoute>
              } />
              <Route path="/relatorios" element={
                <ProtectedRoute>
                  <ModuleBoundary name="Relatórios">
                    <Relatorios />
                  </ModuleBoundary>
                </ProtectedRoute>
              } />
              <Route path="/fiscal" element={
                <ProtectedRoute>
                  <ModuleBoundary name="Fiscal">
                    <Fiscal />
                  </ModuleBoundary>
                </ProtectedRoute>
              } />
              <Route path="/rastreabilidade" element={
                <ProtectedRoute>
                  <ModuleBoundary name="Rastreabilidade">
                    <Rastreabilidade />
                  </ModuleBoundary>
                </ProtectedRoute>
              } />
              <Route path="/estoque" element={
                <ProtectedRoute>
                  <ModuleBoundary name="Estoque">
                    <Estoque />
                  </ModuleBoundary>
                </ProtectedRoute>
              } />
              <Route path="/fretes-taxi" element={
                <ProtectedRoute>
                  <ModuleBoundary name="Frete / Táxi">
                    <FretesTaxi />
                  </ModuleBoundary>
                </ProtectedRoute>
              } />
              <Route path="/licencas" element={
                <ProtectedRoute>
                  <ModuleBoundary name="Licenças">
                    <Licencas />
                  </ModuleBoundary>
                </ProtectedRoute>
              } />
              <Route path="/admin" element={
                <ProtectedRoute requireAdmin>
                  <ModuleBoundary name="Administração">
                    <Admin />
                  </ModuleBoundary>
                </ProtectedRoute>
              } />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
